import { useState } from 'react';
import type { InvoiceChangeLogEntry } from '@erp/rpc';
import { useAPInvoiceChangeLog } from '@/api/rpc/hooks/accounts-payable/useAPInvoices';
import { invoiceActivityIsLoading } from '@/components/accounts-payable/invoice-detail/invoiceActivityIsLoading';

/**
 * The invoice's activity, ordered for reading: scheduled (future) reruns are
 * pinned at the top soonest-first, then the history newest-first.
 */
export function useInvoiceActivity(apInvoiceId: string) {
  const { entries, isFetched, isFetching, error, refetch } =
    useAPInvoiceChangeLog(apInvoiceId);
  const [isRefreshing, setIsRefreshing] = useState(false);

  /** Manual reload: shows the full loading state until the refetch settles. */
  const refresh = () => {
    setIsRefreshing(true);
    return refetch().finally(() => setIsRefreshing(false));
  };

  const scheduled = entries
    .filter((entry) => entry.kind === 'scheduled_run')
    .sort(
      (a, b) =>
        new Date(a.scheduledFor ?? a.createdAt).getTime() -
        new Date(b.scheduledFor ?? b.createdAt).getTime(),
    );
  const history = entries
    .filter((entry) => entry.kind !== 'scheduled_run')
    .sort(
      (a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
    );
  const ordered = [...scheduled, ...history];

  return {
    entries: ordered,
    isLoading:
      isRefreshing ||
      invoiceActivityIsLoading({
        isFetched,
        isFetching,
        entryCount: ordered.length,
      }),
    error,
    refresh,
  };
}

export type InvoiceActivityState = {
  entries: InvoiceChangeLogEntry[];
  isLoading: boolean;
  error: unknown;
};
